"use client";

import type {
  DisplayMeducation,
  DisplayMeducationCard,
  DisplayMeducationGridItem,
  DisplayRecommendationCard,
} from "@health/types";
import {
  Carousel,
  CarouselContent,
  CarouselDots,
  CarouselItem,
  Chip,
  UserMessage,
  WidgetCard,
  WidgetErrorCard,
} from "@health/ui";
import { useEffect, useRef, useState } from "react";
import {
  RecommendationAccordionRow,
  RecommendationBullet,
  RecommendationBulletList,
  RecommendationCtaButton,
  RecommendationCtaRow,
  RecommendationDisclaimer,
  RecommendationFaqSection,
  RecommendationGradientCard,
  RecommendationLabel,
  RecommendationProductImage,
  RecommendationTitle,
} from "./meducation/atoms";

// ---------------------------------------------------------------------------
// Input / output types mirroring the tool schema
// ---------------------------------------------------------------------------

type MeducationInput = {
  mode: "grid" | "carousel" | "card" | "stat" | "recommendation";
  content_id?: string;
  card_index?: number;
  stat?: { value: string; label: string; source?: string };
  skippable?: boolean;
};

type MeducationOutput = {
  action: string;
  selected_treatment_id?: string;
  education_plan?: {
    source: string;
    base: { treatment_id: string; content_id: string; product_id: string };
    education_options: Array<{
      treatment_id: string;
      content_id: string;
      product_id: string;
      label: string;
      subtitle: string;
      preview_image_url: string;
      relevance: string;
    }>;
    topic_tags: string[];
  };
  cards_viewed?: number;
  display_recommendation?: DisplayRecommendationCard;
  /** Set when the recommendation chain is exhausted; agent should call show_product with this id. */
  fallback_product_id?: string;
};

type Props = {
  input: Record<string, unknown>;
  output?: unknown;
  onSubmit: (result: unknown) => void;
  submitted?: boolean;
};

// ---------------------------------------------------------------------------
// CTA chip specs — single source of truth for both the rendered buttons and
// the post-submit user-message label lookup. Updating a label here updates
// both the button and the submitted view automatically.
// ---------------------------------------------------------------------------

type CtaChip = {
  action: string;
  label: string;
  secondary?: boolean;
};

const CAROUSEL_CTA_CHIPS: readonly CtaChip[] = [
  { action: "continue", label: "Continue" },
  { action: "has_questions", label: "I have questions", secondary: true },
];

const CARD_CTA_CHIPS: readonly CtaChip[] = [
  { action: "tell_me_more", label: "Tell me more" },
  { action: "not_interested", label: "Not interested", secondary: true },
  { action: "continue", label: "Continue" },
];

const GRID_SKIP_LABEL = "Continue without add-ons";

function ctaLabelForAction(chips: readonly CtaChip[], action: string | undefined): string | null {
  if (!action) return null;
  return chips.find((c) => c.action === action)?.label ?? null;
}

/**
 * Renders a row of CTA chips when unsubmitted, and the selected chip's label
 * as a UserMessage once submitted — so it's clear which option the user
 * picked without inspecting the surrounding content.
 */
function CtaChipRow({
  chips,
  submitted,
  submittedAction,
  onSelect,
}: {
  chips: readonly CtaChip[];
  submitted: boolean | undefined;
  submittedAction: string | undefined;
  onSelect: (chip: CtaChip) => void;
}) {
  if (submitted) {
    const label = ctaLabelForAction(chips, submittedAction);
    if (!label) return null;
    return (
      <div className="mt-3 flex">
        <UserMessage>{label}</UserMessage>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-2 mt-4">
      {chips.map((chip) => (
        <Chip
          key={chip.action}
          label={chip.label}
          secondary={chip.secondary}
          onClick={() => onSelect(chip)}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Suppression check — exported so callers can return null before mounting
// ---------------------------------------------------------------------------

/**
 * Returns true when the server-side execute returned action:"skip", meaning
 * there is no content to display. Callers (e.g. renderWidget in ChatPanel)
 * should return null to prevent empty wrapper elements in the feed.
 */
export function isMeducationSuppressed(output: unknown): boolean {
  return (
    !!output && typeof output === "object" && (output as Record<string, unknown>).action === "skip"
  );
}

// ---------------------------------------------------------------------------
// Main widget — delegates to sub-components by mode
// ---------------------------------------------------------------------------

export function MeducationWidget({ input, output, onSubmit, submitted }: Props) {
  const { mode } = input as MeducationInput;

  switch (mode) {
    case "grid":
      return (
        <MeducationGrid input={input} output={output} onSubmit={onSubmit} submitted={submitted} />
      );
    case "carousel":
      return (
        <MeducationCarousel
          input={input}
          output={output}
          onSubmit={onSubmit}
          submitted={submitted}
        />
      );
    case "card":
      return (
        <MeducationCard input={input} output={output} onSubmit={onSubmit} submitted={submitted} />
      );
    case "recommendation":
      return (
        <MeducationRecommendationCard
          input={input}
          output={output}
          onSubmit={onSubmit}
          submitted={submitted}
        />
      );
    case "stat":
      return (
        <MeducationStat input={input} output={output} onSubmit={onSubmit} submitted={submitted} />
      );
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Grid mode — mini-card grid for browse & select
// ---------------------------------------------------------------------------

type GridOption = DisplayMeducationGridItem & { relevance?: string };

/**
 * Thumbnail with a label-based fallback when the image fails to load. Shared
 * between the selectable grid items (large, square) and the submitted
 * selection summary (small, compact horizontal) so image loading, fallback
 * styling, and broken-image handling live in one place.
 */
function GridOptionThumbnail({
  option,
  broken,
  onError,
  size,
}: {
  option: GridOption;
  broken: boolean;
  onError: () => void;
  size: "square" | "compact";
}) {
  const containerClass =
    size === "square"
      ? "aspect-square bg-secondary/20 flex items-center justify-center p-3"
      : "w-12 h-12 rounded-lg bg-secondary/20 flex items-center justify-center overflow-hidden shrink-0";
  const fallbackClass =
    size === "square"
      ? "text-sm font-medium text-primary/60 text-center"
      : "text-[10px] font-medium text-primary/60 text-center px-1";
  return (
    <div className={containerClass}>
      {broken ? (
        <span className={fallbackClass}>{option.label}</span>
      ) : (
        <img
          src={option.preview_image_url}
          alt={option.label}
          className="max-h-full max-w-full object-contain"
          loading="lazy"
          decoding="async"
          onError={onError}
        />
      )}
    </div>
  );
}

function MeducationGrid({ input, output, onSubmit, submitted }: Props) {
  const typedOutput = output as MeducationOutput | undefined;
  const plan = typedOutput?.education_plan;
  const options = plan?.education_options ?? [];
  const [selected, setSelected] = useState<string | null>(null);
  const [brokenImages, setBrokenImages] = useState<Set<string>>(() => new Set());
  const markBroken = (id: string) => setBrokenImages((prev) => new Set(prev).add(id));

  // Server returned skip (no matching options) — render nothing and let the
  // agent auto-continue. This is an intentional code path, not an error.
  if (options.length === 0) {
    return null;
  }

  // Submitted state:
  //  - Treatment selected → show a compact thumbnail + label card so the
  //    selection stays visually obvious (an image carries more than a bare
  //    UserMessage here).
  //  - Skip → surface the button label as a UserMessage, matching the
  //    carousel/card CTA pattern.
  if (submitted) {
    const selectedOption = options.find(
      (o) => o.treatment_id === typedOutput?.selected_treatment_id,
    );
    if (selectedOption) {
      return (
        <WidgetCard
          className="px-3 py-3 flex items-center gap-3 text-sm text-primary"
          $padding="none"
        >
          <GridOptionThumbnail
            option={selectedOption}
            broken={brokenImages.has(selectedOption.treatment_id)}
            onError={() => markBroken(selectedOption.treatment_id)}
            size="compact"
          />
          <div className="min-w-0">
            <p className="text-sm font-medium text-primary truncate">{selectedOption.label}</p>
            {selectedOption.subtitle && (
              <p className="text-xs text-text-secondary truncate">{selectedOption.subtitle}</p>
            )}
          </div>
        </WidgetCard>
      );
    }
    if (typedOutput?.action === "skip") {
      return (
        <div className="flex">
          <UserMessage>{GRID_SKIP_LABEL}</UserMessage>
        </div>
      );
    }
    return null;
  }

  const handleSelect = (treatmentId: string) => {
    setSelected(treatmentId);
    onSubmit({
      action: "select_treatment",
      selected_treatment_id: treatmentId,
      education_plan: plan,
      _userSubmitted: true,
    });
  };

  return (
    <div role="radiogroup" aria-label="Treatment options">
      <div className="grid grid-cols-2 gap-3">
        {options.map((item: GridOption) => (
          <button
            key={item.treatment_id}
            type="button"
            role="radio"
            aria-checked={selected === item.treatment_id}
            onClick={() => handleSelect(item.treatment_id)}
            className="rounded-2xl bg-widget-bg overflow-hidden shadow-sm text-left transition-all hover:shadow-md focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 cursor-pointer"
          >
            <GridOptionThumbnail
              option={item}
              broken={brokenImages.has(item.treatment_id)}
              onError={() => markBroken(item.treatment_id)}
              size="square"
            />
            <div className="p-3">
              <p className="text-sm font-medium text-primary">{item.label}</p>
              <p className="text-xs text-text-secondary mt-0.5">{item.subtitle}</p>
            </div>
          </button>
        ))}
      </div>

      {(input as MeducationInput).skippable !== false && (
        <div className="mt-3 flex justify-center">
          <Chip
            label={GRID_SKIP_LABEL}
            secondary
            onClick={() => onSubmit({ action: "skip", education_plan: plan, _userSubmitted: true })}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Carousel mode — swipeable education cards
// ---------------------------------------------------------------------------

function MeducationCarousel({ input, output, onSubmit, submitted }: Props) {
  const typedOutput = output as (MeducationOutput & { display?: DisplayMeducation }) | undefined;
  const [isVisible, setIsVisible] = useState(false);

  const display: DisplayMeducation | undefined =
    typedOutput?.display ??
    ((input as Record<string, unknown>).display as DisplayMeducation | undefined);
  const cards = display?.cards ?? [];

  // Entrance animation
  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  if (cards.length === 0) {
    return <WidgetErrorCard>Education content not available</WidgetErrorCard>;
  }

  return (
    <div
      className="transition-all duration-500"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(14px)",
      }}
    >
      <Carousel
        // `align: start` so partially visible peek cards stay on the right
        // edge instead of jumping to center; `containScroll: trimSnaps` keeps
        // the first card flush-left and the last card flush-right (no
        // overshoot past either end).
        opts={{ align: "start", containScroll: "trimSnaps" }}
        aria-label={display?.name ?? "Treatment education"}
      >
        {/* Override CarouselContent's default -ml-4 inset so the first card
         *  doesn't get clipped against the chat column edge. */}
        <CarouselContent>
          {cards.map((card: DisplayMeducationCard, i: number) => (
            <CarouselItem key={i} className="basis-[min(310px,calc(100vw-48px))]">
              <div className="aspect-4/5 rounded-2xl overflow-hidden shadow-sm bg-secondary/20">
                <img
                  src={card.imageUrl}
                  alt={card.imageAlt}
                  className="w-full h-full object-cover pointer-events-none"
                  loading={i === 0 ? "eager" : "lazy"}
                  decoding="async"
                  draggable={false}
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>

        <CarouselDots ariaLabel="Card" className="mt-3" />
      </Carousel>

      {/* CTA chips when unsubmitted, UserMessage with selected label once submitted */}
      <CtaChipRow
        chips={CAROUSEL_CTA_CHIPS}
        submitted={submitted}
        submittedAction={typedOutput?.action}
        onSelect={(chip) =>
          onSubmit({
            action: chip.action,
            cards_viewed: cards.length,
            // Preserve `display` so the submitted-state re-render can keep
            // showing the carousel cards instead of the "content not available"
            // fallback (the AI SDK replaces the original execute output with
            // this submission, so we have to carry display forward ourselves).
            display: typedOutput?.display ?? display,
            _userSubmitted: true,
          })
        }
      />

      {/* Disclaimer */}
      {display?.disclaimer && (
        <p className="text-xs text-text-secondary mt-3">{display.disclaimer}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card mode — single inline card (lighter touch after grid selection)
// ---------------------------------------------------------------------------

function MeducationCard({ input, output, onSubmit, submitted }: Props) {
  const typedInput = input as MeducationInput;
  const typedOutput = output as (MeducationOutput & { display?: DisplayMeducation }) | undefined;

  const display: DisplayMeducation | undefined =
    typedOutput?.display ??
    ((input as Record<string, unknown>).display as DisplayMeducation | undefined);
  const card = display?.cards?.[typedInput.card_index ?? 0];

  if (!card) {
    return <WidgetErrorCard>Treatment info not available</WidgetErrorCard>;
  }

  return (
    <div>
      <WidgetCard $padding="none" className="overflow-hidden">
        <div className="aspect-[4/3] bg-secondary/20 flex items-center justify-center overflow-hidden">
          <img
            src={card.imageUrl}
            alt={card.imageAlt}
            className="w-full h-full object-cover"
            loading="eager"
            decoding="async"
          />
        </div>
        <div className="p-4">
          <h3
            className="text-lg font-medium text-primary"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {display?.name}
          </h3>
          {display?.subtitle && (
            <p className="text-sm text-text-secondary mt-0.5">{display.subtitle}</p>
          )}
          <p className="text-sm text-primary mt-2">{card.statement}</p>
          {card.bullets && (
            <ul className="mt-2 space-y-1">
              {card.bullets.map((b, i) => (
                <li key={i} className="text-sm text-text-secondary flex items-start gap-2">
                  <span className="text-accent mt-0.5">-</span>
                  {b}
                </li>
              ))}
            </ul>
          )}
        </div>
      </WidgetCard>

      {/* CTA chips when unsubmitted, UserMessage with selected label once submitted */}
      <CtaChipRow
        chips={CARD_CTA_CHIPS}
        submitted={submitted}
        submittedAction={typedOutput?.action}
        onSelect={(chip) =>
          onSubmit({
            action: chip.action,
            // Carry display forward so the submitted-state render can still
            // show the card content. Same rationale as the carousel — the AI
            // SDK's `addToolOutput` replaces the original execute output.
            display: typedOutput?.display ?? display,
            _userSubmitted: true,
          })
        }
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat mode — inline statistic (auto-submit)
// ---------------------------------------------------------------------------

function MeducationStat({ input, onSubmit, submitted }: Props) {
  const { stat } = input as MeducationInput;
  const didSubmit = useRef(false);

  useEffect(() => {
    if (!submitted && !didSubmit.current && stat) {
      didSubmit.current = true;
      onSubmit({ action: "acknowledged", _userSubmitted: true });
    }
  }, [submitted, onSubmit, stat]);

  if (!stat) return null;

  return (
    <div role="figure" aria-label={`${stat.value} ${stat.label}`} className="py-2">
      <div className="flex items-baseline gap-2">
        <span
          className="text-3xl font-bold text-primary"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {stat.value}
        </span>
        <span className="text-sm text-text-secondary">{stat.label}</span>
      </div>
      {stat.source && <p className="text-xs text-text-secondary mt-1">Source: {stat.source}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recommendation mode — personalized treatment recommendation card
// ---------------------------------------------------------------------------

const RECOMMENDATION_CTA_CHIPS: Array<{
  action: string;
  label: string;
  secondary?: boolean;
}> = [
  { action: "yes_eligible", label: "Yes, see if I'm eligible" },
  { action: "another_option", label: "No, I want another option", secondary: true },
];

function MeducationRecommendationCard({ input, output, onSubmit, submitted }: Props) {
  const typedOutput = output as MeducationOutput | undefined;

  if (output === undefined) return null;

  const recommendation = typedOutput?.display_recommendation;

  // Skip with `fallback_product_id` is a server signal to the agent (call
  // show_product), not a render. Suppress the widget either way.
  if (typedOutput?.action === "skip") return null;

  if (!recommendation) {
    return <WidgetErrorCard>Recommendation content not available</WidgetErrorCard>;
  }

  const submittedAction = typedOutput?.action;
  const submittedLabel = RECOMMENDATION_CTA_CHIPS.find((c) => c.action === submittedAction)?.label;

  return (
    <div className="flex flex-col gap-4">
      {/* Gradient recommendation card */}
      <RecommendationGradientCard style={{ background: recommendation.gradient }}>
        {recommendation.productImageUrl && (
          <RecommendationProductImage
            src={recommendation.productImageUrl}
            alt={recommendation.treatmentName}
          />
        )}
        {/* Text content — left ~60%, right ~40% reserved for pill image */}
        <div className="relative z-10 flex flex-col pr-[40%]">
          {/* Label + name grouped (10 px gap matches Figma) */}
          <div className="flex flex-col gap-2.5">
            <RecommendationLabel>{recommendation.label}</RecommendationLabel>
            <RecommendationTitle accentColor={recommendation.treatmentNameAccentColor}>
              {recommendation.treatmentName}
            </RecommendationTitle>
          </div>
          {/* Bullets with ~44 px separation from name (Figma absolute offset = 160 px from top) */}
          <RecommendationBulletList className="mt-11">
            {recommendation.bullets.map((bullet, i) => (
              <RecommendationBullet key={i}>{bullet}</RecommendationBullet>
            ))}
          </RecommendationBulletList>
        </div>
      </RecommendationGradientCard>

      {/* FAQ accordion */}
      <RecommendationFaqSection>
        {recommendation.faq.map((item, i) => (
          <RecommendationAccordionRow key={i} question={item.question} answer={item.answer} />
        ))}
      </RecommendationFaqSection>

      {/* FDA disclaimer */}
      <RecommendationDisclaimer>{recommendation.disclaimer}</RecommendationDisclaimer>

      {/* CTAs — hidden after submission; UserMessage shows what was selected */}
      {submitted && submittedLabel ? (
        <div className="mt-1 flex">
          <UserMessage>{submittedLabel}</UserMessage>
        </div>
      ) : (
        !submitted && (
          <RecommendationCtaRow>
            {RECOMMENDATION_CTA_CHIPS.map((chip) => (
              <RecommendationCtaButton
                key={chip.action}
                $variant={chip.secondary ? "secondary" : "primary"}
                onClick={() =>
                  onSubmit({
                    action: chip.action,
                    display_recommendation: recommendation,
                    _userSubmitted: true,
                  })
                }
              >
                {chip.label}
              </RecommendationCtaButton>
            ))}
          </RecommendationCtaRow>
        )
      )}
    </div>
  );
}
